import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Calendar, ShieldCheck, Clock } from 'lucide-react';
import { companyInfo } from '../data';
import useMeta from '../hooks/useMeta';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchServiceDetail = async () => {
      try {
        setLoading(true);
        const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setService(data);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Fejl ved hentning af service detaljer:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetail();
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-zinc-50 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="h-12 w-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-zinc-600 font-medium">Henter detaljer...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="bg-zinc-50 min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md space-y-6 bg-white p-8 rounded-2xl border border-zinc-200 shadow-sm">
          <h2 className="text-2xl font-semibold text-zinc-900 uppercase">Serviceydelsen blev ikke fundet</h2>
          <p className="text-zinc-600">
            Beklager, men den serviceydelse, du leder efter, findes ikke eller er blevet flyttet.
          </p>
          <Link
            to="/service"
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-3 text-sm font-bold text-white hover:bg-red-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Tilbage til oversigten</span>
          </Link>
        </div>
      </div>
    );
  }

  const title = service.title?.da || service.title || '';
  const desc = service.description?.da || service.description || '';
  const contentHtml = service.content?.da || service.content || '';
  const imgUrl = service.image?.[0] || '';

  useMeta(
    title ? `${title} | LJR Teknik ApS` : 'Serviceydelse | LJR Teknik ApS',
    desc || 'Professionel service og reparation af gaffeltrucks og mindre maskiner.'
  );

  return (
    <div className="bg-zinc-50 min-h-screen pb-16 md:pb-24">
      {/* Detail Header */}
      <section className="bg-zinc-950 text-white py-12 md:py-16 border-b border-zinc-800">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Link
            to="/service"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-zinc-300 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 text-white" />
            <span>Tilbage til alle ydelser</span>
          </Link>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl uppercase">
            {title}
          </h1>
          <p className="text-lg text-zinc-300 max-w-3xl mt-4 leading-relaxed">
            {desc}
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="mx-auto max-w-7xl px-4 lg:px-8 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          {/* Left Column: Image and Rich Text Content */}
          <div className="lg:col-span-2 space-y-8">
            <div className="h-[300px] md:h-[450px] relative rounded-xl overflow-hidden bg-zinc-100">
              <img
                src={imgUrl}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Render HTML content with custom Tailwind v4 selectors */}
            <div
              className="
                prose max-w-none
                [&_p]:text-zinc-600 [&_p]:leading-relaxed [&_p]:mb-6 [&_p]:text-base
                [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-zinc-900 [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:border-b [&_h3]:border-zinc-100 [&_h3]:pb-2
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2.5 [&_ul]:mb-6
                [&_li]:text-zinc-600 [&_li]:text-base
                [&_strong]:text-zinc-900 [&_strong]:font-semibold
              "
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          </div>

          {/* Right Column: Sidebar (Contact & Info) */}
          <div className="space-y-6">
            {/* Quick Booking Card */}
            <div className="bg-zinc-900 text-white rounded-2xl border border-zinc-800 p-6 space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-zinc-800 pb-3 uppercase">
                Bestil service eller eftersyn
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Har du brug for service på dine maskiner? Ring direkte til mig eller send en besked, så vender jeg hurtigt tilbage med en tid.
              </p>
              <div className="space-y-3">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center justify-center gap-2 rounded-lg bg-red-600 py-3 text-sm font-bold text-white hover:bg-red-700 transition-all shadow-md shadow-red-600/10"
                >
                  <Phone className="h-4 w-4" />
                  <span>Ring: {companyInfo.phone}</span>
                </a>
                <Link
                  to="/kontakt"
                  className="group flex items-center justify-center gap-2 rounded-lg border border-white bg-zinc-800 py-3 text-sm font-semibold text-white hover:bg-white hover:text-black transition-all"
                >
                  <Mail className="h-4 w-4 text-white group-hover:text-black transition-colors" />
                  <span>Send en besked</span>
                </Link>
              </div>
            </div>

            {/* Why Choose Us Sidebar Card */}
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4">
              <h3 className="text-base font-semibold text-zinc-900 border-b border-zinc-100 pb-3 uppercase">
                Dine fordele hos mig
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5 text-sm text-zinc-600">
                  <ShieldCheck className="h-5 w-5 text-black shrink-0 mt-0.5" />
                  <span>Professionelt og lovpligtigt eftersyn</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-zinc-600">
                  <Clock className="h-5 w-5 text-black shrink-0 mt-0.5" />
                  <span>Hurtig udrykning ved nedbrud</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-zinc-600">
                  <Calendar className="h-5 w-5 text-black shrink-0 mt-0.5" />
                  <span>Fleksible serviceaftaler</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
